import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FiSearch, FiUser, FiDollarSign, FiStar } from 'react-icons/fi'
import { RootState } from '@/store'

const HowItWorksPage: React.FC = () => {
    const navigate = useNavigate()
    const { user: currentUser, isAuthenticated } = useSelector((state: RootState) => state.auth)
    const steps = [
        {
            icon: <FiSearch className="w-8 h-8" />,
            title: "Browse & Discover",
            description: "Search through thousands of gig opportunities across various categories. Filter by location, budget, and skills to find the perfect match.",
            details: [
                "Advanced search filters",
                "Category-based browsing",
                "Location-specific gigs",
                "Skill-based matching"
            ]
        },
        {
            icon: <FiUser className="w-8 h-8" />,
            title: "Apply & Connect",
            description: "Create a compelling profile showcasing your skills and experience. Apply to gigs with personalized proposals that stand out.",
            details: [
                "Professional profile creation",
                "Portfolio showcase",
                "Custom application letters",
                "Real-time messaging"
            ]
        },
        {
            icon: <FiDollarSign className="w-8 h-8" />,
            title: "Work & Earn",
            description: "Complete projects according to agreed terms and timelines. Receive secure payments through our trusted payment system.",
            details: [
                "Secure payment processing",
                "Milestone-based payments",
                "Project management tools",
                "Deadline tracking"
            ]
        },
        {
            icon: <FiStar className="w-8 h-8" />,
            title: "Review & Grow",
            description: "Build your reputation through client reviews and ratings. Use feedback to improve and attract more high-quality opportunities.",
            details: [
                "Two-way rating system",
                "Detailed feedback",
                "Reputation building",
                "Success tracking"
            ]
        }
    ]

    const features = [
        {
            title: "For Freelancers",
            benefits: [
                "Access to global opportunities",
                "Flexible work schedules",
                "Competitive compensation",
                "Skill development",
                "Network building",
                "Portfolio growth"
            ]
        },
        {
            title: "For Employers",
            benefits: [
                "Access to skilled talent",
                "Cost-effective solutions",
                "Project-based hiring",
                "Quality assurance",
                "Scalable workforce",
                "Fast turnaround"
            ]
        }
    ]

    // Handle button clicks based on user role
    const handleFindGigsClick = () => {
        // Browse gigs is open to everyone - no login required
        navigate('/gigs')
    }

    const handlePostGigClick = () => {
        if (!isAuthenticated) {
            // Redirect to login if not authenticated
            navigate('/login')
            return
        }

        if (currentUser?.role === 'employer') {
            // Employers can create gigs
            navigate('/dashboard/gigs/create')
        } else if (currentUser?.role === 'worker') {
            // Workers can apply to gigs - take them to gigs with focus on applications
            navigate('/gigs')
        }
    }

    // Dynamic button text based on user role
    const getButtonText = () => {
        if (!isAuthenticated) {
            return {
                primary: 'Browse Gigs',
                secondary: 'Post a Gig'
            }
        }

        if (currentUser?.role === 'worker') {
            return {
                primary: 'Browse Gigs',
                secondary: 'Apply for Gigs'  // Different text but same destination - different intent
            }
        } else if (currentUser?.role === 'employer') {
            return {
                primary: 'Browse Gigs',
                secondary: 'Post a Gig'
            }
        }

        return {
            primary: 'Browse Gigs',
            secondary: 'Post a Gig'
        }
    }

    const buttonTexts = getButtonText()

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Hero Section */}
            <section className="bg-white dark:bg-gray-800 py-16 sm:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-8 sm:p-10 lg:p-12 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
                        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 sm:mb-6 text-gray-900 dark:text-white">How SideGO Works</h1>
                        <p className="text-base sm:text-lg md:text-body-lg max-w-3xl mx-auto text-gray-700 dark:text-gray-300">
                            SideGO connects talented freelancers with businesses and individuals looking for
                            skilled professionals. Our platform makes it easy to find, hire, and work on
                            projects that match your expertise and goals.
                        </p>
                    </div>
                </div>
            </section>

            {/* Steps Section */}
            <section className="py-16 sm:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 sm:mb-16">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900 dark:text-white">Simple Steps to Success</h2>
                        <p className="text-base sm:text-lg md:text-body-lg max-w-2xl mx-auto text-gray-700 dark:text-gray-300">
                            Getting started on SideGO is straightforward. Follow these four steps to
                            begin your freelancing journey or find the talent you need.
                        </p>
                    </div>

                    {/* First row: 2 steps */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                        {steps.slice(0, 2).map((step, index) => (
                            <div key={index} className="relative">
                                <div className="text-center">
                                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full mb-6">
                                        {step.icon}
                                    </div>
                                    <h3 className="text-lg sm:text-xl md:text-2xl font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">{step.title}</h3>
                                    <p className="text-sm sm:text-base md:text-body mb-4 sm:mb-6 text-gray-600 dark:text-gray-400">{step.description}</p>

                                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 sm:p-4">
                                        <ul className="space-y-2">
                                            {step.details.map((detail, detailIndex) => (
                                                <li key={detailIndex} className="flex items-center text-xs sm:text-sm md:text-base text-gray-700 dark:text-gray-300">
                                                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                                                    {detail}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>


                            </div>
                        ))}
                    </div>

                    {/* Second row: remaining 2 steps */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {steps.slice(2, 4).map((step, index) => (
                            <div key={index + 2} className="relative">
                                <div className="text-center">
                                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full mb-6">
                                        {step.icon}
                                    </div>
                                    <h3 className="text-lg sm:text-xl md:text-2xl font-semibold mb-3 sm:mb-4 text-gray-900 dark:text-white">{step.title}</h3>
                                    <p className="text-sm sm:text-base md:text-body mb-4 sm:mb-6 text-gray-600 dark:text-gray-400">{step.description}</p>

                                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 sm:p-4">
                                        <ul className="space-y-2">
                                            {step.details.map((detail, detailIndex) => (
                                                <li key={detailIndex} className="flex items-center text-xs sm:text-sm md:text-base text-gray-700 dark:text-gray-300">
                                                    <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                                                    {detail}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>


                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="bg-white dark:bg-gray-800 py-16 sm:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 sm:mb-16">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900 dark:text-white">Why Choose SideGO?</h2>
                        <p className="text-base sm:text-lg md:text-body-lg max-w-2xl mx-auto text-gray-700 dark:text-gray-300">
                            Our platform is designed to benefit both freelancers and employers,
                            creating a win-win environment for successful collaborations.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {features.map((feature, index) => (
                            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-6 sm:p-8 shadow-sm border border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg sm:text-xl md:text-2xl font-semibold mb-4 sm:mb-6 text-center text-gray-900 dark:text-white">{feature.title}</h3>
                                <div className="grid grid-cols-1 gap-3 sm:gap-4">
                                    {feature.benefits.map((benefit, benefitIndex) => (
                                        <div key={benefitIndex} className="flex items-center">
                                            <div className="w-2 h-2 bg-success-500 rounded-full mr-3 flex-shrink-0"></div>
                                            <span className="text-sm sm:text-base md:text-body text-gray-600 dark:text-gray-400">{benefit}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Safety & Security Section */}
            <section className="py-16 sm:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 sm:mb-16">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900 dark:text-white">Safety & Security</h2>
                        <p className="text-base sm:text-lg md:text-body-lg max-w-2xl mx-auto text-gray-700 dark:text-gray-300">
                            Your safety and security are our top priorities. We've implemented multiple
                            measures to ensure a safe and trustworthy platform for everyone.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg flex items-center justify-center mx-auto mb-4">
                                <FiUser className="w-6 h-6" />
                            </div>
                            <h3 className="text-lg sm:text-xl md:text-2xl font-semibold mb-2 sm:mb-3 text-gray-900 dark:text-white">Verified Profiles</h3>
                            <p className="text-sm sm:text-base md:text-body text-gray-600 dark:text-gray-400">
                                All users go through identity verification to ensure authenticity and build trust
                                within our community.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg flex items-center justify-center mx-auto mb-4">
                                <FiDollarSign className="w-6 h-6" />
                            </div>
                            <h3 className="text-lg sm:text-xl md:text-2xl font-semibold mb-2 sm:mb-3 text-gray-900 dark:text-white">Secure Payments</h3>
                            <p className="text-sm sm:text-base md:text-body text-gray-600 dark:text-gray-400">
                                We use industry-standard encryption and trusted payment processors to protect
                                your financial information.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg flex items-center justify-center mx-auto mb-4">
                                <FiStar className="w-6 h-6" />
                            </div>
                            <h3 className="text-lg sm:text-xl md:text-2xl font-semibold mb-2 sm:mb-3 text-gray-900 dark:text-white">Review System</h3>
                            <p className="text-sm sm:text-base md:text-body text-gray-600 dark:text-gray-400">
                                Our comprehensive review and rating system helps maintain quality and
                                accountability across all interactions.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="bg-white dark:bg-gray-800 py-16 sm:py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-8 sm:p-10 lg:p-12 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">Ready to Get Started?</h2>
                        <p className="text-base sm:text-lg md:text-body-lg mb-6 sm:mb-8 text-gray-700 dark:text-gray-300">
                            Join thousands of freelancers and businesses who are already using SideGO
                            to achieve their goals. Create your account today and start your journey.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={handleFindGigsClick}
                                className="btn-hero bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-700"
                            >
                                {buttonTexts.primary}
                            </button>
                            <button
                                onClick={handlePostGigClick}
                                className="btn-hero bg-transparent border-2 border-primary-600 text-primary-600 hover:bg-primary-50 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-900/20"
                            >
                                {buttonTexts.secondary}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default HowItWorksPage